<template>
  <div class="checkout-page">
    <!-- Hero Section -->
    <section class="page-hero">
      <div class="container">
        <h1 class="page-title">Checkout</h1>
        <p class="page-subtitle">Complete your order securely</p>
      </div>
    </section>

    <!-- Checkout Content -->
    <section class="checkout-section">
      <div class="container">
        <div class="row g-4">
          <!-- Left: Checkout Form -->
          <div class="col-lg-8">
            <!-- Step 1: Customer Information -->
            <div class="checkout-card" data-aos="fade-up">
              <div class="card-header">
                <div class="step-number">1</div>
                <h3>Contact Information</h3>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Full Name *</label>
                    <input 
                      type="text" 
                      v-model="customerDetails.name" 
                      class="form-control"
                      placeholder="Enter your name"
                      required
                    >
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Email Address *</label>
                    <input 
                      type="email" 
                      v-model="customerDetails.email" 
                      class="form-control"
                      placeholder="your@email.com"
                      required
                    >
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Phone Number *</label>
                    <input 
                      type="tel" 
                      v-model="customerDetails.phone" 
                      class="form-control"
                      placeholder="+91 98765 43210"
                      required
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 2: Shipping Address -->
            <div class="checkout-card" data-aos="fade-up" data-aos-delay="100">
              <div class="card-header">
                <div class="step-number">2</div>
                <h3>Shipping Address</h3>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Full Name *</label>
                    <input 
                      type="text" 
                      v-model="shippingAddress.fullName" 
                      class="form-control"
                      placeholder="Recipient name"
                    >
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Phone Number *</label>
                    <input 
                      type="tel" 
                      v-model="shippingAddress.phone" 
                      class="form-control"
                      placeholder="Delivery contact"
                    >
                  </div>
                  <div class="col-12">
                    <label class="form-label">Address Line 1 *</label>
                    <input 
                      type="text" 
                      v-model="shippingAddress.addressLine1" 
                      class="form-control"
                      placeholder="House/Flat No., Building Name"
                    >
                  </div>
                  <div class="col-12">
                    <label class="form-label">Address Line 2</label>
                    <input 
                      type="text" 
                      v-model="shippingAddress.addressLine2" 
                      class="form-control"
                      placeholder="Street, Area"
                    >
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Landmark</label>
                    <input 
                      type="text" 
                      v-model="shippingAddress.landmark" 
                      class="form-control"
                      placeholder="Near..."
                    >
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">City *</label>
                    <input 
                      type="text" 
                      v-model="shippingAddress.city" 
                      class="form-control"
                      placeholder="City"
                    >
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">State *</label>
                    <select v-model="shippingAddress.state" class="form-control">
                      <option value="">Select State</option>
                      <option value="Gujarat">Gujarat</option>
                      <option value="Maharashtra">Maharashtra</option>
                      <option value="Delhi">Delhi</option>
                      <!-- Add more states -->
                    </select>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Pincode *</label>
                    <input 
                      type="text" 
                      v-model="shippingAddress.pincode" 
                      class="form-control"
                      placeholder="380001"
                      maxlength="6"
                    >
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Country</label>
                    <input 
                      type="text" 
                      v-model="shippingAddress.country" 
                      class="form-control"
                      readonly
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Order Notes -->
            <div class="checkout-card" data-aos="fade-up" data-aos-delay="200">
              <div class="card-header">
                <div class="step-number">3</div>
                <h3>Order Notes (Optional)</h3>
              </div>
              <div class="card-body">
                <textarea 
                  v-model="customerNotes" 
                  class="form-control" 
                  rows="3"
                  placeholder="Any special instructions for your order..."
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Right: Order Summary -->
          <div class="col-lg-4">
            <div class="order-summary-sticky">
              <div class="checkout-card order-summary" data-aos="fade-up">
                <div class="card-header">
                  <h3>Order Summary</h3>
                </div>
                <div class="card-body">
                  <!-- Cart Items -->
                  <div class="summary-items">
                    <div 
                      v-for="item in cartItems" 
                      :key="item.productId" 
                      class="summary-item"
                    >
                      <img 
                        :src="item.productDetail.mainImageUrl" 
                        :alt="item.productDetail.name"
                        class="item-image"
                      >
                      <div class="item-details">
                        <h6>{{ item.productDetail.name }}</h6>
                        <p>Qty: {{ item.qty }}</p>
                      </div>
                      <div class="item-price">
                        ₹{{ (item.productDetail.offerPrice || item.productDetail.price) * item.qty }}
                      </div>
                    </div>
                  </div>

                  <hr>

                  <!-- Pricing Breakdown -->
                  <div class="price-breakdown">
                    <div class="price-row">
                      <span>Subtotal</span>
                      <span>₹{{ pricing.itemsTotal }}</span>
                    </div>
                    <div class="price-row">
                      <span>Shipping</span>
                      <span>{{ pricing.shippingCharges === 0 ? 'FREE' : '₹' + pricing.shippingCharges }}</span>
                    </div>
                    <div v-if="pricing.discount > 0" class="price-row discount">
                      <span>Discount</span>
                      <span>-₹{{ pricing.discount }}</span>
                    </div>
                    <hr>
                    <div class="price-row total">
                      <span>Total</span>
                      <span>₹{{ pricing.totalAmount }}</span>
                    </div>
                  </div>

                  <!-- Place Order Button -->
                  <button 
                    @click="initiateCheckout" 
                    class="btn-place-order"
                    :disabled="isProcessing || !isFormValid"
                  >
                    <span v-if="!isProcessing">
                      <i class="fas fa-lock"></i>
                      Place Order
                    </span>
                    <span v-else>
                      <i class="fas fa-spinner fa-spin"></i>
                      Processing...
                    </span>
                  </button>

                  <p class="secure-note">
                    <i class="fas fa-shield-alt"></i>
                    Secure payment powered by Razorpay
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useToast } from 'vue-toast-notification';
import OrderService from '../services/OrderService';
import ProductService from '../services/ProductService';

export default {
  name: 'CheckoutPage',
  data() {
    return {
      customerDetails: {
        name: '',
        email: '',
        phone: ''
      },
      shippingAddress: {
        fullName: '',
        phone: '',
        alternatePhone: '',
        addressLine1: '',
        addressLine2: '',
        landmark: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      customerNotes: '',
      cartItems: [],
      pricing: {
        itemsTotal: 0,
        shippingCharges: 0,
        tax: 0,
        discount: 0,
        couponCode: null,
        couponDiscount: 0,
        totalAmount: 0
      },
      isProcessing: false,
    }
  },
  computed: {
    isFormValid() {
        // Email validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        // Phone validation: exactly 10 digits, no spaces or special characters
        const phoneRegex = /^[0-9]{10}$/;
        
        // Validate customer phone
        const isCustomerPhoneValid = phoneRegex.test(this.customerDetails.phone);
        
        // Validate shipping phone
        const isShippingPhoneValid = phoneRegex.test(this.shippingAddress.phone);
        
        // Validate email
        const isEmailValid = emailRegex.test(this.customerDetails.email);
        
        return (
            this.customerDetails.name &&
            isEmailValid &&
            isCustomerPhoneValid &&
            this.shippingAddress.fullName &&
            isShippingPhoneValid &&
            this.shippingAddress.addressLine1 &&
            this.shippingAddress.city &&
            this.shippingAddress.state &&
            this.shippingAddress.pincode &&
            this.cartItems.length > 0
        );
    }
  },
  methods: {
    validateEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    },
    
    validatePhone(phone) {
        const phoneRegex = /^[0-9]{10}$/;
        return phoneRegex.test(phone);
    },
    async loadCart() {
      const $toast = useToast();
      try {
        const response = await ProductService.getCartItems();
        this.cartItems = response.data.data;
        this.calculatePricing();
      } catch (error) {
        $toast.error('Failed to load cart');
        this.$router.push('/cart');
      }
    },
    
    calculatePricing() {
      // Calculate items total
      this.pricing.itemsTotal = this.cartItems.reduce((total, item) => {
        const price = item.productDetail.offerPrice || item.productDetail.price;
        return total + (price * item.qty);
      }, 0);
      
      // Calculate shipping (free above ₹999)
      this.pricing.shippingCharges = this.pricing.itemsTotal >= 999 ? 0 : 99;
      
      // Calculate total
      this.pricing.totalAmount = 
        this.pricing.itemsTotal + 
        this.pricing.shippingCharges - 
        this.pricing.discount - 
        this.pricing.couponDiscount;
    },
    
    async initiateCheckout() {
      const $toast = useToast();
      
      if (!this.isFormValid) {
        $toast.error('Please fill all required fields');
        return;
      }
      
      this.isProcessing = true;
      
      try {
        // Step 1: Create order on backend
        const orderResponse = await OrderService.createOrder({
          customerDetails: this.customerDetails,
          shippingAddress: this.shippingAddress,
          cartItems: this.cartItems,
          pricing: this.pricing,
          customerNotes: this.customerNotes
        });
        
        const { order, razorpayOrder, customer } = orderResponse.data.data;
        
        // Step 2: Open Razorpay payment gateway
        const options = {
          key: process.env.VUE_APP_RAZORPAY_KEY, // Fixed: matches your existing env variable
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          name: "Bee's Atelier",
          description: `Order #${order.orderNumber}`,
          order_id: razorpayOrder.id,
          prefill: {
            name: customer.name,
            email: customer.email,
            contact: customer.phone
          },
          theme: {
            color: "#76ACAF"
          },
          handler: async (response) => {
            // Payment successful - verify on backend
            await this.verifyPayment(response, order._id);
          },
          modal: {
            ondismiss: () => {
              this.isProcessing = false;
              $toast.info('Payment cancelled');
            }
          }
        };
        
        const rzp = new window.Razorpay(options);
        
        // Handle payment failures
        rzp.on("payment.failed", function (response) {
          console.error('Payment failed:', response.error);
          $toast.error(response.error.description || 'Payment failed');
        });
        
        rzp.open();
        
      } catch (error) {
        console.error('Checkout Error:', error);
        $toast.error(error.response?.data?.response_msg || 'Checkout failed');
        this.isProcessing = false;
      }
    },
    
    async verifyPayment(razorpayResponse, orderId) {
      const $toast = useToast();
      
      try {
        const verifyResponse = await OrderService.verifyPayment({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature,
          orderId: orderId
        });
        
        $toast.success('Payment successful! Your order has been placed.');
        
        // Redirect to order confirmation page
        this.$router.push({
          name: 'Order Success',
          query: { orderId: orderId } // Using query instead of params
        });
        
      } catch (error) {
        console.error('Payment Verification Error:', error);
        $toast.error('Payment verification failed. Please contact support.');
        this.isProcessing = false;
      }
    },
    
    // Auto-fill shipping address same as customer details
    autoFillShipping() {
      if (!this.shippingAddress.fullName && this.customerDetails.name) {
        this.shippingAddress.fullName = this.customerDetails.name;
      }
      if (!this.shippingAddress.phone && this.customerDetails.phone) {
        this.shippingAddress.phone = this.customerDetails.phone;
      }
    }
  },
  watch: {
    'customerDetails.name': 'autoFillShipping',
    'customerDetails.phone': 'autoFillShipping'
  },
  mounted() {
    this.loadCart();
    
    // Initialize AOS
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 600,
        once: true
      });
    }
    
    // Load Razorpay script
    if (!document.getElementById('razorpay-script')) {
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      document.body.appendChild(script);
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');

.checkout-page {
  font-family: 'Inter', sans-serif;
  background: #f8fafb;
  min-height: 100vh;
}

/* Hero */
.page-hero {
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  padding: 60px 0 40px 0;
  margin-top: 80px;
  color: white;
  text-align: center;
}

.page-title {
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.page-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
}

/* Checkout Section */
.checkout-section {
  padding: 60px 0 100px 0;
}

.checkout-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  margin-bottom: 24px;
  overflow: hidden;
  text-align: left;
}

.card-header {
  padding: 24px;
  border-bottom: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 16px;
}

.step-number {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.card-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
}

.card-body {
  padding: 24px;
}

/* Form Styles */
.form-label {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.form-control {
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: rgb(118, 172, 175);
  box-shadow: 0 0 0 3px rgba(118, 172, 175, 0.1);
}

/* Order Summary */
.order-summary-sticky {
  position: sticky;
  top: 100px;
}

.order-summary .card-header {
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
}

.order-summary .card-header h3 {
  color: white;
  margin: 0;
}

.summary-items {
  max-height: 300px;
  overflow-y: auto;
}

.summary-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}

.summary-item:last-child {
  border-bottom: none;
}

.item-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  flex-shrink: 0;
}

.item-details {
  flex: 1;
}

.item-details h6 {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
}

.item-details p {
  font-size: 0.85rem;
  color: #718096;
  margin: 0;
}

.item-price {
  font-weight: 700;
  color: #2d3748;
  font-size: 1.05rem;
}

/* Price Breakdown */
.price-breakdown {
  margin-top: 16px;
}

.price-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 1rem;
  color: #4a5568;
}

.price-row.discount {
  color: #48bb78;
}

.price-row.total {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  padding-top: 16px;
}

/* Place Order Button */
.btn-place-order {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.btn-place-order:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(118, 172, 175, 0.4);
}

.btn-place-order:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.secure-note {
  text-align: center;
  font-size: 0.85rem;
  color: #718096;
  margin-top: 12px;
  margin-bottom: 0;
}

.secure-note i {
  color: rgb(118, 172, 175);
  margin-right: 6px;
}

/* Mobile Responsive */
@media (max-width: 991px) {
  .page-hero {
    margin-top: 70px;
    padding: 40px 0 30px 0;
  }
  
  .checkout-section {
    padding: 40px 0 60px 0;
  }
  
  .order-summary-sticky {
    position: static;
  }
  
  .card-header {
    padding: 20px;
  }
  
  .card-body {
    padding: 20px;
  }
}
</style>